import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useTodoListOperations } from './useTodoListOperations';
import { useTodoTasks } from './useTodoTasks';
import { useTodoSubscription } from './useTodoSubscription';
import { TodoList, Task } from './types/todoTypes';

// Types are now imported from todoTypes.ts

export function useTodoLists(roomId: string) {
  const [lists, setLists] = useState<TodoList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initialLoadRef = useRef(true);

  // Initialize operation hooks
  const { 
    loading: listOpLoading, 
    error: listOpError, 
    addList: addListOp, 
    updateList: updateListOp, 
    deleteList: deleteListOp 
  } = useTodoListOperations(roomId);

  const { 
    loading: taskOpLoading, 
    error: taskOpError, 
    addTask: addTaskOp, 
    updateTask: updateTaskOp, 
    deleteTask: deleteTaskOp 
  } = useTodoTasks();

  // Function to update room activity
  const updateRoomActivity = useCallback(async () => {
    if (!roomId) return;
    
    try {
      const { error } = await supabase.rpc('update_room_activity', {
        room_id_param: roomId
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error updating room activity:', error);
    }
  }, [roomId]);

  // Transform database task to local task format
  const transformTask = useCallback((task: any): Task => ({
    id: task.id,
    list_id: task.list_id,
    text: task.text,
    completed: task.completed,
    deadline: task.deadline ? new Date(task.deadline) : null,
    created_at: task.created_at,
    updated_at: task.updated_at,
  }), []);

  // Transform database list to local list format
  const transformList = useCallback((list: any): TodoList => ({
    id: list.id,
    title: list.title,
    room_id: list.room_id,
    created_at: list.created_at,
    updated_at: list.updated_at,
    tasks: list.tasks ? list.tasks.map(transformTask) : [],
  }), [transformTask]);

  // Fetch all todo lists and their tasks for a room
  const fetchTodoLists = useCallback(async (force = false) => {
    if (!roomId) {
      console.log('No roomId provided, skipping fetch');
      setLoading(false);
      return;
    }
    
    // Skip if it's not the initial load and not forced
    if (!initialLoadRef.current && !force) {
      console.log('Skipping fetch - not initial load and not forced');
      return;
    }
    
    console.log('Fetching todo lists for room:', roomId);
    setLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('todo_lists_with_tasks')
        .select('*')
        .eq('room_id', roomId);

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      console.log('Fetched data:', data);
      const transformedLists = (data || []).map(transformList);
      console.log('Transformed lists:', transformedLists);
      
      setLists(prevLists => {
        console.log('Previous lists before setting:', prevLists.map(list => list.id));
        console.log('New lists to set:', transformedLists.map(list => list.id));
        return transformedLists;
      });
      setError(null);
      initialLoadRef.current = false;
    } catch (err) {
      console.error('❌ Error fetching todo lists:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch todo lists'));
      setLists([]); // Reset lists on error
    } finally {
      setLoading(false);
      console.log('Finished loading todo lists');
    }
  }, [roomId, transformList]);

  // Add a new todo list
  const addList = useCallback(async (title: string) => {
    try {
      await updateRoomActivity();
      console.log('Adding new list with title:', title);
      const newList = await addListOp(title);
      if (newList) {
        console.log('New list created, waiting for real-time update...');
        // Force a refresh after a short delay to ensure we have the latest data
        setTimeout(() => fetchTodoLists(true), 500);
        return transformList({ ...newList, tasks: [] });
      }
      return null;
    } catch (error) {
      console.error('Error in addList:', error);
      throw error;
    }
  }, [addListOp, transformList, fetchTodoLists]);

  // Delete a todo list
  const deleteList = useCallback(async (listId: string) => {
    return await deleteListOp(listId);
  }, [deleteListOp]);

  // Add a task to a list
  const addTask = useCallback(async (listId: string, text: string, deadline?: Date | null) => {
    try {
      await updateRoomActivity();
      console.log('Adding new task to list:', { listId, text, deadline });
      const newTask = await addTaskOp(listId, text, deadline);
      if (newTask) {
        console.log('New task created, waiting for real-time update...');
        // Force a refresh after a short delay to ensure we have the latest data
        setTimeout(() => fetchTodoLists(true), 300);
        return transformTask(newTask);
      }
      return null;
    } catch (error) {
      console.error('Error in addTask:', error);
      throw error;
    }
  }, [addTaskOp, transformTask, fetchTodoLists]);

  // Toggle task completion status
  const toggleTask = useCallback(async (listId: string, taskId: string, completed: boolean) => {
    try {
      await updateRoomActivity();
      console.log('Toggling task completion:', { taskId, completed });
      const updatedTask = await updateTaskOp(taskId, { completed });
      if (updatedTask) {
        console.log('Task toggled, waiting for real-time update...');
        // Force a refresh after a short delay to ensure we have the latest data
        setTimeout(() => fetchTodoLists(true), 200);
        return transformTask(updatedTask);
      }
      return null;
    } catch (error) {
      console.error('Error in toggleTask:', error);
      throw error;
    }
  }, [updateTaskOp, transformTask, fetchTodoLists]);

  // Delete a task
  const deleteTask = useCallback(async (taskId: string) => {
    return await deleteTaskOp(taskId);
  }, [deleteTaskOp]);

  // Combine loading and error states from all operations
  const combinedLoading = loading || listOpLoading || taskOpLoading;
  const combinedError = error || listOpError || taskOpError;

  // Memoize the list IDs to prevent unnecessary subscription updates
  const listIds = useMemo(() => {
    const ids = lists.map(list => list.id);
    console.log('Current list IDs:', ids);
    return ids;
  }, [lists]);
  
  // Memoize the onUpdate handler to prevent unnecessary re-renders
  const handleUpdate = useCallback((updatedLists: TodoList[]) => {
    console.log('===== RECEIVED TODO LIST UPDATE =====');
    console.log('Updated lists count:', updatedLists.length);
    console.log('Updated list IDs:', updatedLists.map(list => list.id));
    
    // Make sure we have a valid array before proceeding
    if (!Array.isArray(updatedLists)) {
      console.warn('❌ Received non-array update, skipping...');
      return;
    }
    
    // Only update if we have actual changes
    setLists(prevLists => {
      console.log('Previous list IDs:', prevLists.map(list => list.id));
      
      // If we have specific updated lists, merge them with existing state
      if (updatedLists.length > 0) {
        const updatedListIds = new Set(updatedLists.map(list => list.id));
        console.log('Updated list IDs (set):', Array.from(updatedListIds));
        
        const filteredLists = prevLists.filter(list => !updatedListIds.has(list.id));
        console.log('Filtered lists (not updated):', filteredLists.map(list => list.id));
        
        const mergedLists = [...filteredLists, ...updatedLists].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        
        // Only update if there are actual changes
        const hasChanges = JSON.stringify(mergedLists) !== JSON.stringify(prevLists);
        console.log('Has changes after merge:', hasChanges);
        
        if (hasChanges) {
          console.log('✅ Applying merged todo lists update');
          console.log('Merged list IDs:', mergedLists.map(list => list.id));
          return mergedLists;
        } else {
          console.log('⏩ No changes detected after merge, skipping update');
        }
      } else if (prevLists.length > 0) {
        // Only clear if we actually have lists to clear
        console.log('⚠️ No todo lists found in update, but have previous lists. Clearing state.');
        return [];
      } else {
        console.log('⏩ No lists in update and no previous lists, no action needed');
      }
      
      // Return previous state if no changes
      console.log('↩️ Returning previous state');
      return prevLists;
    });
  }, []);
  
  // Set up the subscription with stable dependencies
  const { refresh } = useTodoSubscription({
    roomId,
    listIds,
    onUpdate: handleUpdate
  });
  
  // Add a manual refresh function that can be called if needed
  const manualRefresh = useCallback(() => {
    console.log('Manual refresh triggered');
    refresh();
  }, [refresh]);
  


  // Initial fetch when roomId changes
  useEffect(() => {
    initialLoadRef.current = true;
    if (roomId) {
      console.log('Running initial fetch for room:', roomId);
      fetchTodoLists().then(() => {
        console.log('Initial fetch completed');
        // Update activity on initial load
        updateRoomActivity();
      });
    } else {
      setLists([]);
      setLoading(false);
    }
  }, [roomId, fetchTodoLists]);

  return {
    lists,
    loading: combinedLoading,
    error: combinedError,
    addList,
    updateList: updateListOp,
    deleteList,
    addTask,
    updateTask: updateTaskOp,
    deleteTask,
    toggleTask,
    refresh: manualRefresh,
  };
}
